import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { chatService } from '../services/chatService';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import NotificationBell from '../components/NotificationBell';
import Chat from '../components/Chat';

const Support = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    loadConversations();
  }, [user]);

  const loadConversations = async () => {
    try {
      setLoading(true);
      const response = await chatService.getConversations();
      if (response.success) {
        setConversations(response.conversations || []);
        // Sélectionner la première conversation ouverte si disponible
        if (!selectedConversation && response.conversations.length > 0) {
          const openConversation = response.conversations.find(c => c.status === 'open');
          if (openConversation) {
            setSelectedConversation(openConversation);
          }
        }
      }
    } catch (error) {
      console.error('Erreur lors du chargement des conversations:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateConversation = async () => {
    try {
      setCreating(true);
      const response = await chatService.getOrCreateConversation();
      if (response.success) {
        setSelectedConversation(response.conversation);
        await loadConversations();
      } else {
        toast.error(`Erreur: ${response.error}`);
      }
    } catch (error) {
      console.error('Erreur lors de la création de la conversation:', error);
      toast.error('Erreur lors de la création de la conversation');
    } finally {
      setCreating(false);
    }
  };

  const formatDate = (dateValue) => {
    const date =
      dateValue && typeof dateValue.toDate === 'function'
        ? dateValue.toDate()
        : new Date(dateValue);
    if (Number.isNaN(date.getTime())) return '—';
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / 60000);

    if (minutes < 1) return 'À l\'instant';
    if (minutes < 60) return `Il y a ${minutes} min`;
    if (date.toDateString() === now.toDateString()) {
      return date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
    }
    return date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' });
  };

  const getOtherParticipant = (conversation) => {
    const uids = conversation.participantUids;
    if (!Array.isArray(uids) || uids.length === 0) return null;
    const selfId = user?.id || user?.uid;
    const others = uids.filter((id) => id && id !== selfId);
    if (others.length === 0) return null;
    return { uid: others[0] };
  };

  return (
    <div>
      <Navbar />

      <div className="container" style={{ maxWidth: '1400px', margin: '40px auto', padding: '20px' }}>
        <h1 style={{ marginBottom: '30px', color: '#FF0000', fontSize: '32px', fontWeight: '700' }}>
          💬 Support Client
        </h1>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '20px', height: '600px' }}>
          {/* Liste des conversations */}
          <div style={{
            backgroundColor: '#fff',
            borderRadius: '8px',
            boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          }}>
            <div style={{
              padding: '15px 20px',
              borderBottom: '1px solid #e0e0e0',
              backgroundColor: '#f8f9fa',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}>
              <h3 style={{ margin: 0, fontSize: '16px', color: '#333' }}>Conversations</h3>
              <button
                onClick={handleCreateConversation}
                disabled={creating}
                className="btn btn-primary"
                style={{
                  padding: '8px 15px',
                  fontSize: '12px',
                  whiteSpace: 'nowrap',
                }}
              >
                {creating ? 'Création...' : '+ Nouvelle'}
              </button>
            </div>

            <div style={{ flex: 1, overflowY: 'auto' }}>
              {loading ? (
                <div style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
                  Chargement...
                </div>
              ) : conversations.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
                  <p>Aucune conversation</p>
                  <button
                    onClick={handleCreateConversation}
                    className="btn btn-primary"
                    style={{ marginTop: '10px' }}
                  >
                    Créer une conversation
                  </button>
                </div>
              ) : (
                conversations.map((conversation) => {
                  const otherParticipant = getOtherParticipant(conversation);
                  const isSelected = selectedConversation?._id === conversation._id;
                  const unreadCount = user.role === 'client' 
                    ? conversation.unreadCount?.client || 0
                    : conversation.unreadCount?.agent || 0;

                  return (
                    <div
                      key={conversation._id}
                      onClick={() => setSelectedConversation(conversation)}
                      style={{
                        padding: '15px',
                        borderBottom: '1px solid #e0e0e0',
                        cursor: 'pointer',
                        backgroundColor: isSelected ? '#f0f0f0' : '#fff',
                        transition: 'background-color 0.2s',
                      }}
                      onMouseEnter={(e) => {
                        if (!isSelected) e.currentTarget.style.backgroundColor = '#f8f8f8';
                      }}
                      onMouseLeave={(e) => {
                        if (!isSelected) e.currentTarget.style.backgroundColor = '#fff';
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <div style={{ flex: 1 }}>
                          <div style={{
                            fontWeight: unreadCount > 0 ? 'bold' : 'normal',
                            color: '#333',
                            marginBottom: '5px',
                          }}>
                            {otherParticipant
                              ? `Utilisateur (${String(otherParticipant.uid).slice(0, 8)}…)`
                              : 'Équipe Maxfly'}
                          </div>
                          {conversation.orderId && (
                            <div style={{ fontSize: '12px', color: '#666', marginBottom: '5px' }}>
                              Commande: {conversation.orderId.orderNumber || conversation.orderId}
                            </div>
                          )}
                          <div style={{ fontSize: '12px', color: '#999' }}>
                            {formatDate(conversation.lastMessageAt)}
                          </div>
                        </div>
                        {unreadCount > 0 && (
                          <div style={{
                            backgroundColor: '#FF0000',
                            color: '#fff',
                            borderRadius: '50%',
                            width: '20px',
                            height: '20px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '11px',
                            fontWeight: 'bold',
                          }}>
                            {unreadCount}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>

          {/* Zone de chat */}
          <div style={{ height: '100%' }}>
            {selectedConversation ? (
              <Chat
                conversationId={selectedConversation._id}
                onClose={() => setSelectedConversation(null)}
              />
            ) : (
              <div style={{
                height: '100%',
                backgroundColor: '#fff',
                borderRadius: '8px',
                boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexDirection: 'column',
                padding: '40px',
              }}>
                <div style={{ fontSize: '48px', marginBottom: '20px' }}>💬</div>
                <h3 style={{ color: '#333', marginBottom: '10px' }}>Sélectionnez une conversation</h3>
                <p style={{ color: '#666', textAlign: 'center', marginBottom: '20px' }}>
                  Choisissez une conversation dans la liste ou créez-en une nouvelle pour commencer à chatter avec notre équipe de support.
                </p>
                <button
                  onClick={handleCreateConversation}
                  className="btn btn-primary"
                >
                  Créer une nouvelle conversation
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Support;

